import React from 'react';
import { DemeterPageType, DemeterTableDefinitionType, UnitOfMeasure } from '../../../../Generated/Raven-Demeter';
import { ISeasonalChartProps } from '../../../Components/Charts/Seasonal/SeasonalChart';
import { IRegionCommoditySelection } from '../../../Components/Navigation/Hooks/useRegionCommodityNavigationHook';
import useLanguage from '../../../Services/Language/useLanguageHook';
import { IWithPageElementProps } from '../DashboardCustomizationDefinitions';

const withSeasonalChartFromPageElement = (SeasonalChart: React.ComponentType<ISeasonalChartProps>): React.FC<IWithPageElementProps> => {
    const Wrapped: React.FC<IWithPageElementProps> = (props) => {
        if (!props.pageElement) {
            return <> </>;
        }

        const { demeterPageElementGuid, pageType, region, subRegion, commodity, subCommodity, extraParameters, dataFrequency, unitOfMeasure } =
            props.pageElement;
        const [translations] = useLanguage();

        let pageTitle: string;
        let tableDefinitionType: DemeterTableDefinitionType;
        switch (pageType) {
            case DemeterPageType.Imports:
                pageTitle = translations.imports.title;
                tableDefinitionType = DemeterTableDefinitionType.CommodityMonthlyImportsTable;
                break;
            case DemeterPageType.Production:
                pageTitle = translations.production.title;
                tableDefinitionType = DemeterTableDefinitionType.CommodityMonthlyProductionTable;
                break;
            case DemeterPageType.Exports:
                pageTitle = translations.exports.title;
                tableDefinitionType = DemeterTableDefinitionType.CommodityMonthlyExportsTable;
                break;
            case DemeterPageType.BalanceTable:
                pageTitle = translations.balanceTable.title;
                tableDefinitionType = DemeterTableDefinitionType.CommodityYearlyBalanceTable;
                break;
            case DemeterPageType.Stocks:
                pageTitle = translations.stocks.title;
                tableDefinitionType = DemeterTableDefinitionType.CommodityMonthlyStockTable;
                break;
            case DemeterPageType.Domestics:
                pageTitle = translations.domestics.title;
                tableDefinitionType = DemeterTableDefinitionType.CommodityMonthlyDomesticTable;
                break;
            default:
                pageTitle = '';
                tableDefinitionType = DemeterTableDefinitionType.AllFundamentalsAndPrices;
                break;
        }

        const getExtraParameters = () => {
            if (props.widgetOptions?.extraParameters) {
                return props.widgetOptions.extraParameters;
            }

            return extraParameters && Object.keys(extraParameters).length > 0 ? JSON.stringify(extraParameters) : null;
        };

        const regionCommoditySelection = {
            region,
            subRegion,
            commodity: subCommodity ?? commodity,
            extraParameters: getExtraParameters(),
            dataFrequency,
        } as IRegionCommoditySelection;

        const chartProps: ISeasonalChartProps = {
            title: pageTitle,
            customTitle: props.customTitle,
            tableDefinitionType,
            regionCommoditySelection,
            aggregateRegions: [],
            unitOfMeasure: unitOfMeasure ?? UnitOfMeasure.MetricTon,
            testId: `dashboard-seasonal-chart-${demeterPageElementGuid}`,
            dragIndicatorIcon: props.dragIndicatorIcon,
            isInDashboardEditMode: props.isInDashboardEditMode,
            maximizeMinimizeButton: props.maximizeMinimizeButton,
            maxWidthHeightRatio: props.maxWidthHeightRatio,
            handleMoreOptionsSelection: props.handleMoreOptionsSelection,
            track: {
                eventCategory: 'Dashboard',
                eventAction: 'SeasonalChart',
                eventValue: { tableDefinitionType, regionCommoditySelection },
            },
        };

        return <SeasonalChart {...chartProps} />;
    };

    return Wrapped;
};

export default withSeasonalChartFromPageElement;
